<template>
    <form ref="theForm" method="get" :action="generateFormRoute()" @submit="makeReport">
        <div class="row">
            <div class="col-md-6">
                <AdminBlock title="Collection Start">
                    <div class="input-group">
                        <input ref="periodStartInput" type="text" placeholder="Select a date" class="form-control js-flatpickers" id="collection_start" name="collection_start" v-model="period_start" required />
                        <span class="input-group-text"><i class="fa fa-calendar"></i></span>
                    </div>
                </AdminBlock>
            </div>
            <div class="col-md-6">
                <AdminBlock title="Collection End">
                    <div class="input-group">
                        <input ref="periodEndInput" type="text" placeholder="Select a date" class="form-control js-flatpickers" id="collection_end" name="collection_end" v-model="period_end" required />
                        <span class="input-group-text"><i class="fa fa-calendar"></i></span>
                    </div>
                </AdminBlock>
            </div>
        </div>
        <div class="row">
            <div class="col-6">
                <AdminBlock title="Options">
                    <div class="row">
                        <div class="col-6">
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" name="only_ready_to_invoice" id="only_ready_to_invoice" value="1" v-model="onlyReadyToInvoice">
                                <label class="form-check-label" for="only_ready_to_invoice">
                                    Only include Jobs with status Ready to Invoice
                                </label>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" name="only_customers_with_multiple_jobs" id="only_customers_with_multiple_jobs" value="1" v-model="onlyCustomersWithMultipleJobs">
                                <label class="form-check-label" for="only_customers_with_multiple_jobs">
                                    Only include Customers with two or more Jobs
                                </label>
                            </div>
                        </div>
                    </div>
                </AdminBlock>
            </div>
            <div class="col-6">
                <AdminBlock>
                    <button
                        type="button"
                        class="btn btn-sm btn-primary w-100 mb-2 me-2"
                        @click="makeReport($event, 'preview')"
                        name="output"
                        value="preview"
                        :disabled="isPreviewLoading"
                        :aria-busy="isPreviewLoading"
                    >
                        {{ isPreviewLoading ? 'Loading…' : 'Preview' }}
                    </button>
                    <div class="row">
                        <div class="col-6">
                            <button type="submit" class="btn btn-sm btn-secondary w-100" name="output" value="download">Download</button>
                        </div>
                        <div class="col-6">
                            <button type="submit" class="btn btn-sm btn-alt-info w-100" name="output" value="download-csv">Download CSV</button>
                        </div>
                    </div>
                </AdminBlock>
            </div>
        </div>

        <AdminBlock title="Preview">
            <iframe
                ref="reportPreview"
                v-if="reportPreviewSrc"
                :src="reportPreviewSrc"
                class="w-100"
                :class="{ 'mt-4 border': previewReady }"
                :style="{ 'height': previewReady ? '500px' : '100px' }"
                @load="onPreviewLoad"
            />
        </AdminBlock>
    </form>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import flatpickr from "flatpickr";
import { DateTime } from "luxon";
import AdminBlock from "@//components/movements/components/AdminBlock.vue";

const props = defineProps({
    lastFriday: String,
    nextFriday: String,
});

const theForm = ref();
const period_start = ref("");
const period_end = ref("");
const periodStartInput = ref();
const periodEndInput = ref();

const reportPreview = ref();
const isPreviewLoading = ref(false);
const reportPreviewSrc = ref("");
const previewReady = ref(false);
const onlyReadyToInvoice = ref(false);
const onlyCustomersWithMultipleJobs = ref(false);

const generateFormRoute = () => {
    return route('movements.reports.grouped-jobs.report');
}

const onPreviewLoad = () => {
    isPreviewLoading.value = false;
    previewReady.value = true;
};

const makeReport = function (ev, output) {
    output = output || 'download';

    if (output === 'download') {
        return;
    }

    ev.preventDefault();

    if (! theForm.value.checkValidity()) {
        console.error('finish the form please');
        return;
    }

    isPreviewLoading.value = true;

    const data = new FormData(theForm.value);

    if (! data.has('output')) {
        data.append('output', output);
    }

    const queryString = new URLSearchParams();

    for (const key of data.keys()) {
        queryString.append(key, data.get(key));
    }

    const newValue = `${generateFormRoute()}?${queryString.toString()}`;

    // if the value doesn't change then the load event is never fired and the button is disabled forever
    if (reportPreviewSrc.value !== newValue) {
        reportPreviewSrc.value = newValue;
    } else {
        // add a little delay so the UI looks like its changing even though it isn't
        setTimeout(() => {
            isPreviewLoading.value = false;
        }, 100);
    }
};

onMounted(() => {
    nextTick(() => {
        flatpickr('.js-flatpickers', {
            dateFormat: "Y-m-d",
            enableTime: false,
            altInput: true,
        });
        const today = DateTime.now();
        // Previous Friday (if today is Friday, use today)
        const prevFriday = today.weekday >= 5 ? today.set({ weekday: 5 }) : today.minus({ weeks: 1 }).set({ weekday: 5 });
        // Next Friday (if today is Friday, use today)
        const nextFriday = today.weekday <= 5 ? today.set({ weekday: 5 }) : today.plus({ weeks: 1 }).set({ weekday: 5 });
        period_start.value = prevFriday.toFormat("yyyy-MM-dd");
        period_end.value = nextFriday.toFormat("yyyy-MM-dd");
        periodStartInput.value._flatpickr.setDate(period_start.value);
        periodEndInput.value._flatpickr.setDate(period_end.value);
    });
});
</script>
